// New venue (spec/13 §13, F-ORG-01): a short Stepper in a centred dialog — Name → Where → Confirm (BRAND.md §6.1,
// "steps, not a wall of fields"). The contract needs a name, a slug and a timezone; the address is
// optional. The venue starts in `setup` with the organisation's currency, and its profile, hours and
// photos are filled in later from its own settings. Once it exists the owner is taken to its Setup
// (spec/13 §13.1, F-HQ-03), where the rest of getting it open is laid out, rather than left with a toast.

import type { CreateVenue, Venue } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Stepper, TextField, useToast, FormSection } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { fetchMyVenues } from '../../api/me';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { queryKeys } from '../../api/queries';
import { Facts, Notice } from '../../ui';
import { useCreateVenue } from './queries';

interface Draft {
  readonly name: string;
  readonly slug: string;
  /** Whether the slug was typed rather than made from the name. */
  readonly slugEdited: boolean;
  readonly timezone: string;
  readonly line1: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
}

const EMPTY: Draft = { name: '', slug: '', slugEdited: false, timezone: 'Asia/Kolkata', line1: '', locality: '', region: '', postalCode: '', country: 'IN' };

/** The contract's `VenueSlugSchema`: lower-case letters, digits and single hyphens, 3–60 long. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A slug made from a name: "Playground HSR Layout" → `playground-hsr-layout`. */
export function slugFrom(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

function isTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return value.includes('/') || value === 'UTC';
  } catch {
    return false;
  }
}

export function CreateVenueDialog({ organizationId, open, onClose }: { readonly organizationId: string; readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} eyebrow={t('venues.create.eyebrow')} title={t('venues.create.title')} size="md" dirty="auto">
      {open ? <CreateVenueFlow organizationId={organizationId} onDone={onClose} /> : null}
    </Dialog>
  );
}

/**
 * After a venue is made: the member's venues read again — the venue's pages resolve its slug against them — then its
 * Setup. When they cannot be read, the toast and the list are all there is, as before.
 */
function useOpenNewVenueSetup() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async (venue: Venue): Promise<void> => {
    try {
      const mine = await queryClient.fetchQuery({ queryKey: queryKeys.myVenues, queryFn: () => fetchMyVenues(api), staleTime: 0 });
      if (mine.some((one) => one.id === venue.id)) await navigate({ to: '/v/$venueSlug/setup', params: { venueSlug: venue.slug } });
    } catch {
      // Stay where we are: the venue is in the list.
    }
  };
}

function CreateVenueFlow({ organizationId, onDone }: { readonly organizationId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateVenue(organizationId);
  const openSetup = useOpenNewVenueSetup();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft | 'form', string>>>({});

  const update = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const hasAddress = draft.line1 !== '' || draft.locality !== '' || draft.region !== '' || draft.postalCode !== '';

  const checkName = (): boolean => {
    const next: typeof errors = {};
    if (draft.name.trim() === '') next.name = t('venues.create.nameRequired');
    if (draft.slug.length < 3 || !SLUG.test(draft.slug)) next.slug = t('venues.create.slugInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const checkWhere = (): boolean => {
    const next: typeof errors = {};
    if (!isTimezone(draft.timezone)) next.timezone = t('venues.create.timezoneInvalid');
    if (hasAddress) {
      if (draft.line1.trim() === '') next.line1 = t('venues.create.addressPart');
      if (draft.locality.trim() === '') next.locality = t('venues.create.addressPart');
      if (draft.region.trim() === '') next.region = t('venues.create.addressPart');
      if (draft.postalCode.trim().length < 3) next.postalCode = t('venues.create.addressPart');
    }
    if (!/^[A-Z]{2}$/.test(draft.country)) next.country = t('venues.create.countryInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = () => {
    const body: CreateVenue = {
      name: draft.name.trim(),
      slug: draft.slug,
      timezone: draft.timezone,
      ...(hasAddress
        ? { address: { line1: draft.line1.trim(), locality: draft.locality.trim(), region: draft.region.trim(), postalCode: draft.postalCode.trim(), country: draft.country } }
        : {}),
    };
    create.mutate(body, {
      onSuccess: (venue: Venue) => {
        show({ tone: 'ok', title: t('venues.create.done', { name: venue.name }) });
        onDone();
        void openSetup(venue);
      },
      onError: (failure) => {
        if (problemCode(failure) === PROBLEM.slugTaken) {
          setErrors({ slug: t('venues.create.slugTaken') });
          setStep(0);
          return;
        }
        // A validation answer names the fields: say each beside its input, on the step that holds it.
        const fields: Partial<Record<keyof Draft, string>> = {};
        for (const key of ['name', 'slug', 'timezone'] as const) {
          const message = problem.fieldMessage(failure, key);
          if (message !== undefined) fields[key] = message;
        }
        for (const key of ['line1', 'locality', 'region', 'postalCode', 'country'] as const) {
          const message = problem.fieldMessage(failure, `address.${key}`);
          if (message !== undefined) fields[key] = message;
        }
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          setStep(fields.name !== undefined || fields.slug !== undefined ? 0 : 1);
          return;
        }
        setErrors({ form: problem.text(failure, { fallback: t('venues.create.failed') }) });
      },
    });
  };

  const next = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === 0 && checkName()) setStep(1);
    else if (step === 1 && checkWhere()) setStep(2);
    else if (step === 2) submit();
  };

  const error = (key: keyof Draft) => (errors[key] === undefined ? {} : { error: errors[key] });

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'name', label: t('venues.create.stepName') },
          { id: 'where', label: t('venues.create.stepWhere') },
          { id: 'confirm', label: t('venues.create.stepConfirm') },
        ]}
      />
      <form className="console-form" aria-label={t('venues.create.title')} noValidate onSubmit={next}>
        {step === 0 ? (
          <FormSection key="name"
            forceOpen={errors.slug !== undefined}
            more={
              <TextField
                label={t('venues.create.slug')}
                description={t('venues.create.slugHint')}
                required
                maxLength={60}
                value={draft.slug}
                onChange={(event) => update({ slug: event.target.value.toLowerCase(), slugEdited: true })}
                {...error('slug')}
              />
            }
          >
            <TextField
              label={t('venues.create.name')}
              required
              maxLength={120}
              value={draft.name}
              onChange={(event) => update({ name: event.target.value, ...(draft.slugEdited ? {} : { slug: slugFrom(event.target.value) }) })}
              {...error('name')}
            />
          </FormSection>
        ) : step === 1 ? (
          <FormSection key="where"
            forceOpen={errors.line1 !== undefined || errors.locality !== undefined || errors.region !== undefined || errors.postalCode !== undefined}
            moreLabel={t('venues.create.addAddress')}
            more={
              <>
                <TextField label={t('venues.create.line1')} showOptional value={draft.line1} onChange={(event) => update({ line1: event.target.value })} {...error('line1')} />
                <div className="console-form__row">
                  <TextField label={t('venues.create.locality')} showOptional value={draft.locality} onChange={(event) => update({ locality: event.target.value })} {...error('locality')} />
                  <TextField label={t('venues.create.region')} showOptional value={draft.region} onChange={(event) => update({ region: event.target.value })} {...error('region')} />
                </div>
                <TextField label={t('venues.create.postalCode')} showOptional value={draft.postalCode} onChange={(event) => update({ postalCode: event.target.value })} {...error('postalCode')} />
              </>
            }
          >
            <TextField label={t('venues.create.timezone')} description={t('venues.create.timezoneHint')} required value={draft.timezone} onChange={(event) => update({ timezone: event.target.value.trim() })} {...error('timezone')} />
            <TextField label={t('venues.create.country')} required maxLength={2} value={draft.country} onChange={(event) => update({ country: event.target.value.toUpperCase() })} {...error('country')} />
          </FormSection>
        ) : (
          <>
            <Facts
              items={[
                { key: 'name', label: t('venues.create.name'), value: draft.name.trim() },
                { key: 'slug', label: t('venues.create.slug'), value: <span className="console-num">{draft.slug}</span> },
                { key: 'timezone', label: t('venues.create.timezone'), value: draft.timezone },
                {
                  key: 'address',
                  label: t('venues.create.address'),
                  value: hasAddress ? [draft.line1, draft.locality, draft.region, draft.postalCode].map((part) => part.trim()).join(', ') : t('venues.create.noAddress'),
                },
              ]}
            />
            <Notice role="note">{t('venues.create.confirmHint')}</Notice>
          </>
        )}
        {errors.form === undefined ? null : <Notice tone="danger" role="alert" title={errors.form} />}
        <div className="console-form__actions console-form__actions--end">
          {step === 0 ? (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={create.isPending}>
            {step === 2 ? t('venues.create.submit') : t('common.next')}
          </Button>
        </div>
      </form>
    </div>
  );
}
